// src/components/Profile/NotificationSummary.jsx

import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import './css/notificationSummary.css';
import notificationTranslations from './translations.json';

const NotificationSummary = () => {
  const [todayStats, setTodayStats] = useState({
    newReservations: 0,
    changedReservations: 0,
    cancelledReservations: 0
  });

  useEffect(() => {
    const calculateTodayStats = () => {
      // Get today's date in multiple formats for comparison
      const today = new Date();
      const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      
      // Also create DD-MM-YYYY format for comparison
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const todayStringDDMMYYYY = `${day}-${month}-${year}`;
      
      // Get start and end of today for timestamp comparison
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
      const endOfDay = startOfDay + 24 * 60 * 60 * 1000 - 1; // End of day
      
      // Get notifications from localStorage
      const storedNotifications = localStorage.getItem('notificationsCache');
      if (!storedNotifications) {
        console.log('No notifications found in localStorage');
        return;
      }

      try {
        const notifications = JSON.parse(storedNotifications);
        console.log('=== NOTIFICATION SUMMARY DEBUG ===');
        console.log('Total notifications found:', notifications.length);
        console.log('Today strings for comparison:', {
          todayString,
          todayStringDDMMYYYY,
          startOfDay: new Date(startOfDay).toISOString(),
          endOfDay: new Date(endOfDay).toISOString()
        });
        
        // Log all notifications first
        console.log('All notifications:', notifications.map(n => ({
          id: n._id || n.reservationId,
          event: n.event,
          date: n.date,
          currentDate: n.currentDate,
          creationTimestamp: n.creationTimestamp,
          reservationType: n.reservationType
        })));
        
        // Filter notifications based on event type and conditions
        const todayNotifications = notifications.filter((notification, index) => {
          console.log(`\n--- Processing notification ${index + 1} ---`);
          console.log('Notification data:', {
            id: notification._id || notification.reservationId,
            event: notification.event,
            date: notification.date,
            currentDate: notification.currentDate,
            creationTimestamp: notification.creationTimestamp,
            reservationType: notification.reservationType
          });
          
          // Skip auth notifications and gift card purchases first
          if (notification.event === 'giftcard_purchase') {
            console.log('❌ Skipping: auth notification or giftcard purchase');
            return false;
          }
          
          // Check if the reservation date matches today (handle multiple date formats)
          let isReservationToday = false;
          if (notification.date) {
            console.log('Checking notification.date:', notification.date);
            // Check YYYY-MM-DD format
            const matchesYYYYMMDD = notification.date === todayString;
            console.log(`  vs todayString (${todayString}):`, matchesYYYYMMDD);
            
            // Check DD-MM-YYYY format
            const matchesDDMMYYYY = notification.date === todayStringDDMMYYYY;
            console.log(`  vs todayStringDDMMYYYY (${todayStringDDMMYYYY}):`, matchesDDMMYYYY);
            
            isReservationToday = matchesYYYYMMDD || matchesDDMMYYYY;
          } else {
            console.log('No notification.date field');
          }
          
          // Also check currentDate field if it exists
          if (!isReservationToday && notification.currentDate) {
            console.log('Checking notification.currentDate:', notification.currentDate);
            const currentDateMatchesDDMMYYYY = notification.currentDate === todayStringDDMMYYYY;
            const currentDateMatchesYYYYMMDD = notification.currentDate === todayString;
            console.log(`  vs todayStringDDMMYYYY (${todayStringDDMMYYYY}):`, currentDateMatchesDDMMYYYY);
            console.log(`  vs todayString (${todayString}):`, currentDateMatchesYYYYMMDD);
            
            isReservationToday = currentDateMatchesDDMMYYYY || currentDateMatchesYYYYMMDD;
          } else if (!notification.currentDate) {
            console.log('No notification.currentDate field');
          }
          
          console.log('isReservationToday result:', isReservationToday);
          
          // Check if the notification was created today
          let isNotificationToday = false;
          if (notification.creationTimestamp) {
            const notificationTime = new Date(notification.creationTimestamp).getTime();
            console.log('Checking creationTimestamp:', {
              creationTimestamp: notification.creationTimestamp,
              notificationTime,
              notificationTimeReadable: new Date(notificationTime).toISOString(),
              startOfDay,
              endOfDay,
              isInRange: notificationTime >= startOfDay && notificationTime <= endOfDay
            });
            isNotificationToday = notificationTime >= startOfDay && notificationTime <= endOfDay;
          } else {
            console.log('No creationTimestamp field');
          }
          
          console.log('isNotificationToday result:', isNotificationToday);
          
          // Apply different logic based on event type
          let shouldInclude = false;
          
          if (notification.event === 'creation') {
            // For creation events: BOTH reservation date AND notification creation must be today
            shouldInclude = isReservationToday && isNotificationToday;
            console.log('Creation event - requires BOTH conditions to be true');
            console.log('Logic: isReservationToday AND isNotificationToday');
          } else if (notification.event === 'update' || notification.event === 'deletion') {
            // For update/deletion events: EITHER reservation date OR notification creation can be today
            shouldInclude = isReservationToday && isNotificationToday;
            console.log('Update/deletion event - requires EITHER condition to be true');
            console.log('Logic: isReservationToday OR isNotificationToday');
          } else {
            // For unknown events, use the OR logic as fallback
            shouldInclude = isReservationToday && isNotificationToday;
            console.log('Unknown event - using fallback OR logic');
          }
          
          console.log('Final decision - shouldInclude:', shouldInclude);
          console.log('Reason:', {
            event: notification.event,
            isReservationToday,
            isNotificationToday,
            logic: notification.event === 'creation' ? 'AND logic' : 'OR logic'
          });
          
          if (shouldInclude) {
            console.log('✅ INCLUDING this notification');
          } else {
            console.log('❌ EXCLUDING this notification');
          }
          
          return shouldInclude;
        });

        console.log('\n=== FILTERING COMPLETE ===');
        console.log('Filtered today notifications:', todayNotifications.length);
        console.log('Included notifications:', todayNotifications.map(n => ({
          id: n._id || n.reservationId,
          event: n.event,
          date: n.date,
          currentDate: n.currentDate,
          creationTimestamp: n.creationTimestamp
        })));

        // Count different types of events
        const stats = {
          newReservations: 0,
          changedReservations: 0,
          cancelledReservations: 0
        };

        console.log('\n=== COUNTING EVENTS ===');
        todayNotifications.forEach((notification, index) => {
          console.log(`Counting notification ${index + 1}:`, {
            id: notification._id || notification.reservationId,
            event: notification.event
          });
          
          switch (notification.event) {
            case 'creation':
              stats.newReservations++;
              console.log('  → Counted as NEW reservation');
              break;
            case 'update':
              stats.changedReservations++;
              console.log('  → Counted as CHANGED reservation');
              break;
            case 'deletion':
              stats.cancelledReservations++;
              console.log('  → Counted as CANCELLED reservation');
              break;
            default:
              console.log(`  → UNKNOWN event type: ${notification.event}`);
              break;
          }
        });

        console.log('\n=== FINAL RESULTS ===');
        console.log('Final stats:', stats);
        console.log('=== END DEBUG ===\n');
        setTodayStats(stats);
      } catch (error) {
        console.error('Error parsing notifications from localStorage:', error);
      }
    };

    // Calculate stats on component mount
    calculateTodayStats();

    // Set up an interval to check for updates every 30 seconds
    const interval = setInterval(calculateTodayStats, 30000);

    // Listen for localStorage changes (in case notifications are updated from another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'notificationsCache') {
        calculateTodayStats();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Get translations
  const preferredLanguage = localStorage.getItem('preferredLanguage') || 'nl';
  const t = notificationTranslations[preferredLanguage]?.notificationSummary || 
            notificationTranslations['nl'].notificationSummary;

  return (
    <div className="notification-summary">
      <h3 className="notification-summary__title">{t.title}</h3>
      <div className="notification-summary__cards">
        <div className="notification-summary__card notification-summary__card--new">
          <div className="notification-summary__card-icon">
            <FiPlus size={24} />
          </div>
          <div className="notification-summary__card-content">
            <div className="notification-summary__card-number">
              {todayStats.newReservations}
            </div>
            <div className="notification-summary__card-label">
              {t.newReservations}
            </div>
          </div>
        </div>

        <div className="notification-summary__card notification-summary__card--changed">
          <div className="notification-summary__card-icon">
            <FiEdit size={24} />
          </div>
          <div className="notification-summary__card-content">
            <div className="notification-summary__card-number">
              {todayStats.changedReservations}
            </div>
            <div className="notification-summary__card-label">
              {t.changedReservations}
            </div>
          </div>
        </div>

        <div className="notification-summary__card notification-summary__card--cancelled">
          <div className="notification-summary__card-icon">
            <FiTrash2 size={24} />
          </div>
          <div className="notification-summary__card-content">
            <div className="notification-summary__card-number">
              {todayStats.cancelledReservations}
            </div>
            <div className="notification-summary__card-label">
              {t.cancelledReservations}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSummary;